const DAY_FIRST_DATE_PATTERN = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/;

const parseDayFirstDate = (input: string): number | undefined => {
	const dayFirstMatch = input.match(DAY_FIRST_DATE_PATTERN);
	if (!dayFirstMatch) return undefined;

	const day = Number.parseInt(dayFirstMatch[1], 10);
	const month = Number.parseInt(dayFirstMatch[2], 10);
	const year = Number.parseInt(dayFirstMatch[3], 10);

	if (day < 1 || day > 31 || month < 1 || month > 12) return undefined;

	const asUtcMidnight = Date.UTC(year, month - 1, day);
	const parsedDate = new Date(asUtcMidnight);

	return parsedDate.getUTCFullYear() === year &&
		parsedDate.getUTCMonth() === month - 1 &&
		parsedDate.getUTCDate() === day
		? asUtcMidnight
		: undefined;
};

/**
 * Converts a legacy macro date param to a UTC epoch millisecond timestamp.
 *
 * Handles three input shapes inherited from the legacy Java macro storage format:
 *   1. Numeric (number or numeric string) — epoch ms stored directly, returned as-is.
 *   2. Day-first string ("dd/MM/yyyy" or "d-M-yyyy") — the Confluence date picker's
 *      native format. Parsed explicitly rather than via Date.parse to avoid locale-
 *      dependent swaps (e.g. "02/06/2026" read as Feb 6 in US locales).
 *   3. ISO or other parseable string — fallback via Date.parse.
 */
export const toEpoch = (value: string | number | null | undefined): number | undefined => {
	if (value == null) return undefined;
	if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;

	const trimmed = value.trim();
	if (!trimmed) return undefined;

	const numeric = Number(trimmed);
	if (!Number.isNaN(numeric)) return numeric;

	// Date picker values are day-first calendar dates; parse explicitly to avoid
	// locale-dependent Date.parse swaps (e.g. 09/04 interpreted as Sep 4).
	if (DAY_FIRST_DATE_PATTERN.test(trimmed)) {
		return parseDayFirstDate(trimmed);
	}

	const parsed = Date.parse(trimmed);
	return Number.isNaN(parsed) ? undefined : parsed;
};
